/**
 * useActualHistory - React hook for fetching actual challenge score history
 *
 * Returns the historical satScores from ChallengeRanking for chart display.
 * This provides "ground truth" scores separate from predictions.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../services/api'

import { createLogger } from '../utils/logger'

const logger = createLogger('useActualHistory')

/**
 * Maps API errors to user-friendly messages
 */
const getErrorMessage = (err) => {
  if (err.response?.status === 401) return 'Please sign in to view scores'
  if (err.response?.status === 403) return 'Permission denied'
  if (err.response?.status >= 500) return 'Server error. Please try again later'
  if (err.code === 'ERR_NETWORK') return 'Network error. Please check your connection'
  return err.response?.data?.message || 'Unable to load actual score history'
}

/**
 * Hook for fetching actual SAT scores from completed challenges
 *
 * @param {string} strategyType - The strategy type (SAT_MATH or SAT_READING_WRITING)
 * @param {Object} options - Hook configuration
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {number} options.limit - Maximum entries to fetch (default: 30)
 * @returns {Object} Actual score history and methods
 */
export function useActualHistory(strategyType, { autoFetch = true, limit = 30 } = {}) {
  const [data, setData] = useState({
    history: [],
    trend: null,
    currentScore: null
  })
  const [loading, setLoading] = useState(autoFetch)
  const [error, setError] = useState(null)

  const abortControllerRef = useRef(null)

  const fetchHistory = useCallback(async () => {
    if (!strategyType) return

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new AbortController for this request
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setLoading(true)
    setError(null)

    try {
      const response = await api.get(`/predictions/actual-history/${strategyType}`, {
        params: { limit },
        signal: abortController.signal
      })

      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        const responseData = response.data?.data || response.data || {}
        setData({
          history: responseData.history || [],
          trend: responseData.trend || null,
          currentScore: responseData.currentScore || null
        })
      }
    } catch (err) {
      // Ignore abort errors - they're expected when component unmounts or request is superseded
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return
      }
      logger.error(`Error fetching actual history for ${strategyType}:`, err)
      if (!abortController.signal.aborted) {
        setError(getErrorMessage(err))
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false)
      }
    }
  }, [strategyType, limit])

  useEffect(() => {
    if (autoFetch && strategyType) {
      fetchHistory()
    }

    // Cleanup: abort any in-flight request when dependencies change or unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [autoFetch, strategyType, fetchHistory])

  return {
    history: data.history,
    trend: data.trend,
    currentScore: data.currentScore,
    loading,
    error,
    refresh: fetchHistory
  }
}

/**
 * Hook for fetching actual history for both Math and R&W sections
 *
 * @param {Object} options - Hook configuration
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {number} options.limit - Maximum entries per section (default: 30)
 * @returns {Object} Actual score history for both sections
 */
export function useCombinedActualHistory({ autoFetch = true, limit = 30 } = {}) {
  const {
    history: mathHistory,
    trend: mathTrend,
    currentScore: mathCurrentScore,
    loading: mathLoading,
    error: mathError,
    refresh: refreshMath
  } = useActualHistory('SAT_MATH', { autoFetch, limit })

  const {
    history: rwHistory,
    trend: rwTrend,
    currentScore: rwCurrentScore,
    loading: rwLoading,
    error: rwError,
    refresh: refreshRW
  } = useActualHistory('SAT_READING_WRITING', { autoFetch, limit })

  const refresh = useCallback(() => {
    refreshMath()
    refreshRW()
  }, [refreshMath, refreshRW])

  return {
    math: {
      history: mathHistory,
      trend: mathTrend,
      currentScore: mathCurrentScore
    },
    readingWriting: {
      history: rwHistory,
      trend: rwTrend,
      currentScore: rwCurrentScore
    },
    loading: mathLoading || rwLoading,
    error: mathError || rwError,
    refresh
  }
}

export default useActualHistory
